// frontend/src/components/VehicleIdentityCard.js
// Plate, make/model, VIN and kilométrage for the vehicle a form is currently
// about.
//
// The VIN gets a copy button rather than just being printed: it is 17
// characters with no meaningful grouping, and the workshop retypes it into
// supplier part orders. Reading it off the screen is where transcription
// errors come from.
//
// The odometer is shown wherever the card is, and becomes editable only where
// the caller passes onSaveOdometer — the car is in the bay when a devis is
// written, which is the one moment anybody actually reads the dashboard.

import React, { useEffect, useState } from 'react';
import { fmt } from '../theme/iparc';

const vinOf = (v) => (v?.vin || v?.VIN || v?.chassisNumber || '').trim();

// fmt already folds fr-FR's narrow no-break space to a plain one.
const km = (n) => fmt(n);

// "il y a 3 jours" is more useful than a date here: what matters is whether
// the reading is stale, not which Tuesday it was taken.
function staleness(date) {
  if (!date) return { text: 'jamais relevé', stale: true };
  const days = Math.floor((Date.now() - new Date(date).getTime()) / 86400000);
  if (Number.isNaN(days)) return { text: '', stale: false };
  if (days <= 0) return { text: "relevé aujourd'hui", stale: false };
  if (days === 1) return { text: 'relevé hier', stale: false };
  if (days < 30) return { text: `relevé il y a ${days} jours`, stale: false };
  const months = Math.floor(days / 30);
  return { text: `relevé il y a ${months} mois`, stale: months >= 3 };
}

export default function VehicleIdentityCard({
  vehicle,
  label = 'Véhicule',
  className = '',
  onSaveOdometer,   // async (vehicle, km) => void — omit for a read-only card
}) {
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [odoErr, setOdoErr] = useState('');
  const [savingOdo, setSavingOdo] = useState(false);

  // Switching to another car mid-form must not carry the previous car's draft
  // reading into the new one.
  useEffect(() => {
    setEditing(false);
    setDraft('');
    setOdoErr('');
  }, [vehicle?._id]);

  if (!vehicle) return null;

  const vin = vinOf(vehicle);
  const makeModel = [vehicle.brand || vehicle.make, vehicle.model].filter(Boolean).join(' ');
  const current = Number(vehicle.odometerReading) || 0;
  const age = staleness(vehicle.lastOdometerUpdate);

  const saveOdo = async () => {
    const n = Number(draft);
    if (!Number.isFinite(n) || n <= 0) {
      setOdoErr('Kilométrage invalide.');
      return;
    }
    // Mirrors the server rule (PATCH /vehicles/:id refuses it too), but says so
    // before the round trip instead of after.
    if (current && n < current) {
      setOdoErr(`Ne peut pas reculer sous ${km(current)} km.`);
      return;
    }
    setSavingOdo(true);
    setOdoErr('');
    try {
      await onSaveOdometer(vehicle, n);
      setEditing(false);
      setDraft('');
    } catch (e) {
      setOdoErr(e?.message || 'Enregistrement impossible.');
    } finally {
      setSavingOdo(false);
    }
  };

  const copyVin = async () => {
    // Requires a secure context; silently skipped on http rather than thrown.
    try {
      await navigator.clipboard.writeText(vin);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable — the VIN is on screen to read anyway */
    }
  };

  return (
    <div className={`rounded-xl border border-[#241F1B] bg-[#141210] px-4 py-3 ${className}`}>
      <p className="text-[10px] uppercase tracking-[.18em] text-[#6E665C] mb-1">{label}</p>

      <div className="flex items-baseline gap-2 flex-wrap">
        <span className="font-['Space_Grotesk',sans-serif] text-[13px] tracking-[.02em] text-[#F4F1EA]">
          {vehicle.plateNumber || '—'}
        </span>
        {makeModel && <span className="text-[11.5px] text-[#8F877B]">{makeModel}</span>}
      </div>

      <div className="mt-2 pt-2 border-t border-[#1E1B18] flex items-center gap-2 flex-wrap">
        <span className="text-[10px] uppercase tracking-[.16em] text-[#6E665C] shrink-0">VIN</span>
        {vin ? (
          <>
            <span className="font-['Space_Grotesk',sans-serif] text-[12.5px] tracking-[.08em] text-[#C9C3B6] break-all">
              {vin.toUpperCase()}
            </span>
            <button
              type="button"
              onClick={copyVin}
              title="Copier le VIN"
              className="ml-auto h-[22px] px-2 rounded-[6px] border border-[#2B2724] hover:border-[#4E4740]
                         text-[10.5px] text-[#8F877B] hover:text-[#F4F1EA] shrink-0 transition-colors"
            >
              {copied ? 'Copié' : 'Copier'}
            </button>
          </>
        ) : (
          <span className="text-[12px] text-[#6E665C] italic">non renseigné</span>
        )}
      </div>

      {/* ── Kilométrage ── */}
      <div className="mt-2 pt-2 border-t border-[#1E1B18]">
        {editing ? (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-[.16em] text-[#6E665C] shrink-0">KM</span>
            <input
              type="number"
              min={current || 1}
              step="1"
              autoFocus
              value={draft}
              onChange={(e) => { setDraft(e.target.value); setOdoErr(''); }}
              // Enter must not submit the devis the card is sitting inside.
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); saveOdo(); }
                if (e.key === 'Escape') { e.preventDefault(); setEditing(false); setOdoErr(''); }
              }}
              placeholder={current ? String(current) : 'Kilométrage'}
              className="w-[120px] h-[26px] px-2 rounded-[6px] border border-[#2B2724] bg-[#100F0D]
                         text-[#F4F1EA] text-[12.5px] font-['Space_Grotesk',sans-serif] tabular-nums
                         outline-none focus:border-[#4E4740]"
            />
            <button
              type="button"
              onClick={saveOdo}
              disabled={savingOdo}
              className="h-[22px] px-2 rounded-[6px] border border-[#2B3A31] bg-[#121714] text-[10.5px]
                         text-[#74A98D] hover:border-[#74A98D]/50 disabled:opacity-50 transition-colors"
            >
              {savingOdo ? '…' : 'Enregistrer'}
            </button>
            <button
              type="button"
              onClick={() => { setEditing(false); setOdoErr(''); }}
              className="h-[22px] px-2 rounded-[6px] text-[10.5px] text-[#6E665C] hover:text-[#C9C3B6] transition-colors"
            >
              Annuler
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-[.16em] text-[#6E665C] shrink-0">KM</span>
            {current > 0 ? (
              <span className="font-['Space_Grotesk',sans-serif] text-[12.5px] tabular-nums text-[#C9C3B6]">
                {km(current)} km
              </span>
            ) : (
              <span className="text-[12px] text-[#6E665C] italic">non renseigné</span>
            )}
            {age.text && (
              <span className={`text-[10.5px] ${age.stale ? 'text-[#DFA23C]' : 'text-[#6E665C]'}`}>
                {age.text}
              </span>
            )}
            {onSaveOdometer && (
              <button
                type="button"
                onClick={() => { setDraft(current ? String(current) : ''); setEditing(true); }}
                title="Relever le kilométrage maintenant"
                className="ml-auto h-[22px] px-2 rounded-[6px] border border-[#2B2724] hover:border-[#4E4740]
                           text-[10.5px] text-[#8F877B] hover:text-[#F4F1EA] shrink-0 transition-colors"
              >
                Actualiser
              </button>
            )}
          </div>
        )}
        {odoErr && <p className="mt-1.5 text-[10.5px] text-[#E2574B]">{odoErr}</p>}
      </div>
    </div>
  );
}
